import React, { useMemo, useState } from "react";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import SchoolIcon from "@material-ui/icons/School";
import PersonIcon from "@material-ui/icons/Person";
import GroupIcon from "@material-ui/icons/Group";
import InfoIcon from "@material-ui/icons/Info";
import { useDomainFeatures } from "../../context/DomainFeaturesContext";
import { getSupportEmail } from "../../utils/siteBrand";
import "./HomeSections.css";

const buildFaqData = (brandDomain) => ({
  schools: [
    {
      question: "Is Montestory designed only for Montessori schools?",
      answer: `Yes, ${brandDomain} is exclusively developed based on authentic Montessori principles and classroom structure.`,
    },
    {
      question: "How long does it take to set up the application for a new school?",
      answer:
        "Typically takes 1–3 working days. Once registered, your admin account is activated after due diligence.",
    },
    {
      question: "Do we need any special hardware or software to use Montestory?",
      answer:
        "No. Montestory is a web-based application that works on desktops, laptops, tablets, and mobile devices with internet access.",
    },
    {
      question: "Is training provided for staff?",
      answer:
        "Yes. We offer guided onboarding sessions, video tutorials, and ongoing support to help your team make the most of Montestory.",
    },
  ],
  teachers: [
    {
      question: "Can I customize lesson plans according to my classroom needs?",
      answer:
        "Montestory provides preloaded Montessori lesson plans. Custom lesson plan editing is limited based on your school's configuration.",
    },
    {
      question: "How are student activities and progress tracked?",
      answer:
        "Teachers can log daily activities, observations, assignments, and assessments, which are compiled into visual reports and analytics.",
    },
    {
      question: "Can I collaborate with co-teachers or assistants on the platform?",
      answer:
        "Yes. Role-based access allows collaboration while keeping data secure and organized.",
    },
  ],
  parents: [
    {
      question: "Can parents see what their child is doing?",
      answer:
        "Yes. The parent app provides updates on activities, attendance, assignments, and upcoming events.",
    },
    {
      question: "Is my child's data safe?",
      answer:
        "Yes. All data is encrypted and stored securely. We follow strong data protection practices.",
    },
  ],
  general: [
    {
      question: "What kind of support is available?",
      answer:
        "Our support team is available via email and phone during working hours. We also provide documentation and video guides.",
    },
    {
      question: "Is there a trial version?",
      answer:
        "Yes. Schools can try Montestory with limited access before deciding to subscribe.",
    },
  ],
});

const CATEGORIES = [
  { label: "For schools", icon: SchoolIcon, key: "schools" },
  { label: "For teachers", icon: PersonIcon, key: "teachers" },
  { label: "For parents", icon: GroupIcon, key: "parents" },
  { label: "General", icon: InfoIcon, key: "general" },
];

// Category list on the left, one long answer list on the right.
export default function Faq() {
  const { brandDomain } = useDomainFeatures();
  const faqData = useMemo(() => buildFaqData(brandDomain), [brandDomain]);
  const supportEmail = getSupportEmail();
  const [category, setCategory] = useState("schools");
  const [openIndex, setOpenIndex] = useState(0);

  const chooseCategory = (key) => {
    setCategory(key);
    setOpenIndex(0);
  };

  return (
    <section className="hs" aria-labelledby="faq-title">
      <div className="hs__inner fq">
        <aside className="fq__side">
          <p className="hs__eyebrow">Support</p>
          <h2 className="hs__title" id="faq-title">Questions? <span className="lh-blue">Answers.</span></h2>
          <p className="hs__lead">Quick answers for schools, teachers, and parents.</p>

          <div className="fq__cats" role="tablist" aria-label="FAQ categories" aria-orientation="vertical">
            {CATEGORIES.map(({ label, icon: Icon, key }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={category === key}
                className={`fq__cat${category === key ? " is-active" : ""}`}
                onClick={() => chooseCategory(key)}
              >
                <Icon className="fq__cat-icon" />
                <span className="fq__cat-label">{label}</span>
                <span className="fq__cat-count">{faqData[key].length}</span>
              </button>
            ))}
          </div>

          <div className="fq__help">
            <strong>Still have questions?</strong>
            Write to us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a> and we will get back to you.
          </div>
        </aside>

        <div className="fq__list" role="tabpanel">
          {faqData[category].map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={faq.question} className={`fq__item${isOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="fq__q"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                >
                  <span className="fq__q-text">{faq.question}</span>
                  <span className="fq__toggle" aria-hidden>
                    {isOpen ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  </span>
                </button>
                {isOpen ? <p className="fq__a">{faq.answer}</p> : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
